import React, { useState } from 'react';
import Seo from '../components/Seo';
import { motion } from 'framer-motion';
import { Phone, Send, Download } from 'lucide-react';
import { CONTACT } from '../data/constants';
import { PAGE_COPY, SPA_BUTLER_PLANS, SPA_BUTLER_NOTE } from '../data/seoContent';
import { spaButlerLd } from '../data/structuredData';

const ContactForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', interest: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData(e.target);
    try {
      await fetch('/', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, 
        body: new URLSearchParams(data).toString() 
      });
      setSubmitted(true);
    } catch (error) { 
      // Error handled silently - form submission failed
    }
  };

  if (submitted) {
    return (
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }} 
        animate={{ scale: 1, opacity: 1 }} 
        className="bg-green-50 border border-green-200 p-8 text-center"
      >
        <div className="text-green-600 text-5xl mb-4">✓</div>
        <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-green-800 mb-2">Thank You!</h3>
        <p className="text-green-700">We've received your message and will contact you shortly.</p>
      </motion.div>
    );
  }

  return (
    <form 
      name="spa-butler" 
      method="POST" 
      data-netlify="true" 
      netlify-honeypot="bot-field" 
      onSubmit={handleSubmit} 
      className="space-y-4"
    >
      <input type="hidden" name="form-name" value="spa-butler" />
      <p className="hidden"><label>Don't fill this out: <input name="bot-field" /></label></p>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-[#0E3D95] mb-1">Full Name *</label>
          <input 
            type="text" 
            id="name" 
            name="name" 
            required 
            className="w-full px-4 py-3 border border-slate-300 focus:border-[#F88200] focus:ring-1 focus:ring-[#F88200] outline-none transition-colors" 
            value={formData.name} 
            onChange={e => setFormData({...formData, name: e.target.value})} 
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-[#0E3D95] mb-1">Email *</label>
          <input 
            type="email" 
            id="email" 
            name="email" 
            required 
            className="w-full px-4 py-3 border border-slate-300 focus:border-[#F88200] focus:ring-1 focus:ring-[#F88200] outline-none transition-colors" 
            value={formData.email} 
            onChange={e => setFormData({...formData, email: e.target.value})} 
          />
        </div>
      </div>
      
      <div>
        <label htmlFor="phone" className="block text-sm font-semibold text-[#0E3D95] mb-1">Phone</label>
        <input 
          type="tel" 
          id="phone" 
          name="phone" 
          className="w-full px-4 py-3 border border-slate-300 focus:border-[#F88200] focus:ring-1 focus:ring-[#F88200] outline-none transition-colors" 
          value={formData.phone} 
          onChange={e => setFormData({...formData, phone: e.target.value})} 
        />
      </div>
      
      <div>
        <label htmlFor="message" className="block text-sm font-semibold text-[#0E3D95] mb-1">Message *</label>
        <textarea 
          id="message" 
          name="message" 
          rows="4" 
          required 
          className="w-full px-4 py-3 border border-slate-300 focus:border-[#F88200] outline-none resize-none transition-colors" 
          value={formData.message} 
          onChange={e => setFormData({...formData, message: e.target.value})} 
        />
      </div>
      
      <motion.button 
        type="submit" 
        className="btn-primary w-full flex items-center justify-center gap-2" 
        whileHover={{ scale: 1.02 }} 
        whileTap={{ scale: 0.98 }}
      >
        <Send size={18} /> Send Message
      </motion.button>
    </form>
  );
};

const SpaButlerPage = () => {
  const packages = [
    { 
      name: 'Weekly', 
      drains: '4 visits per month · 3 drains per year',
      yearly: '$4,740 per year',
      monthly: '$395 per month',
      featured: false,
      features: ['Weekly service visits', '3 complete drain & refills per year', 'Full water chemistry balancing', '32-point inspection every visit', 'Chemicals included'] 
    },
    { 
      name: 'Bi-Weekly', 
      drains: '2 visits per month · 2 drains per year',
      yearly: '$3,540 per year',
      monthly: '$295 per month',
      featured: true,
      features: ['Service every 2 weeks', '2 complete drain & refills per year', 'Full water chemistry balancing', '32-point inspection every visit', 'Chemicals included'] 
    },
    { 
      name: 'Monthly', 
      drains: '1 visit per month · 1 drain per year',
      yearly: '$2,340 per year',
      monthly: '$195 per month',
      featured: false,
      features: ['Monthly service visits', '1 complete drain & refill per year', 'Full water chemistry balancing', '32-point inspection every visit', 'Chemicals included'] 
    },
  ];

  const inspectionPoints = [
    'GFCI', 'Sensors', 'Heater', 'Pressure switch', 'Pumps', 'Air Blower', 'Ozone',
    'Air Venturis', 'Jet Diverters', 'Lighting System', 'Cover lift Operation',
    'Cover Locking Mechanism', 'Inspect Cover', 'Inspect for leaks',
    'Treat and sanitize hot tub water', 'Water level top-up & adjust (Sanitizer, Ph, Alkalinity, Hardness)',
    'O rings', 'Replace gaskets if needed', 'Tighten loose fittings',
    'Clean and disinfect filter', 'Clean pillows', 'Clean Cover', 'Clean Steps',
    'Clean Exterior', 'Clean Scum line', 'Spa Surface Skimmed',
    'Spa floor and seats vacuumed', 'Report any problems'
  ];

  return (
    <>
      <Seo page="/spa-butler" jsonLd={spaButlerLd(SPA_BUTLER_PLANS)} />
    <div className="pt-36 pb-20" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 20%, #AFEEEE 50%, #98FB98 80%, #F0FFF0 100%)' }} data-testid="spa-butler-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="font-['Barlow_Condensed'] text-4xl md:text-6xl font-bold uppercase text-[#0E3D95] mb-4"
        >
          Spa Butler Hot Tub Cleaning, Maintenance & Repair in Naples
        </motion.h1>
        
        <div className="bg-[#F88200] text-white p-6 mb-8">
          <p className="text-xl font-semibold" data-testid="page-intro">
            {PAGE_COPY.spaButler}
          </p>
        </div>

        <div className="mb-12 overflow-x-auto" data-testid="spa-butler-plans-table">
          <table className="w-full bg-white shadow-md text-left">
            <thead className="bg-[#0E3D95] text-white">
              <tr>{[['Plan', 'Plan'], ['Visits', 'Visits'], ['Price', 'Price'], ['Drain & refills per year', 'Drains/yr']].map(([full, short]) => (
                <th key={full} className="px-3 sm:px-4 py-3 font-semibold text-sm sm:text-base"><span className="hidden sm:inline">{full}</span><span className="sm:hidden">{short}</span></th>
              ))}</tr>
            </thead>
            <tbody>
              {SPA_BUTLER_PLANS.map((p) => (
                <tr key={p.plan} className="border-b border-slate-100">
                  <td className="px-3 sm:px-4 py-3 font-semibold text-sm sm:text-base text-[#0E3D95]">{p.plan}</td>
                  <td className="px-3 sm:px-4 py-3 text-sm sm:text-base text-slate-600">{p.visits}</td>
                  <td className="px-3 sm:px-4 py-3 text-sm sm:text-base font-bold text-[#F88200] whitespace-nowrap">{p.price}</td>
                  <td className="px-3 sm:px-4 py-3 text-sm sm:text-base text-slate-600">{p.drains}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-sm text-slate-500 mt-3">{SPA_BUTLER_NOTE}</p>
        </div>

        {/* Spa Butler Video */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold text-[#0E3D95] mb-4">
            See Our Spa Butler Service in Action
          </h2>
          <div className="max-w-4xl aspect-video rounded-lg overflow-hidden shadow-2xl">
            <video 
              autoPlay 
              muted 
              loop 
              playsInline 
              controls
              className="w-full h-full object-cover"
            >
              <source src="https://customer-assets.emergentagent.com/job_wet-test-preview/artifacts/v0qvpa8s_SPA%20BUTLER.mp4" type="video/mp4" />
            </video>
          </div>
        </motion.div>
        
        <div className="grid md:grid-cols-2 gap-12 mb-12">
          <div>
            <p className="text-lg text-slate-600 mb-6">
              Our service fee is <strong className="text-[#0E3D95]">$175.00 per hour</strong> plus parts. We do charge $175 for service call which is an hour diagnostic fee.
            </p>
            <p className="text-lg text-slate-600 mb-6">
              We understand busy lifestyles and schedules, so let us help take your hot tub water chemistry and preserve the life of your hot tub with regular hot tub maintenance. We are pleased to offer our New Maintenance Packages to save you both time and money and ensure that your investment is protected, whether you already have a hot tub or have purchased a new hot tub from us.
            </p>
            <p className="text-lg text-slate-600 mb-8">
              Please check out our packages below and let us know if you need help to determine which hot tub maintenance schedule is right for you.
            </p>
            
            <div className="bg-[#0E3D95] text-white p-8">
              <h2 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase mb-4">Always Have Clear And Safe Water</h2>
              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#D4AF37] mb-6">Call The Spa Butler Today!</h3>
              <a href={`tel:${CONTACT.phone.replace(/[^0-9]/g, '')}`} className="btn-primary inline-flex items-center gap-2">
                <Phone size={20} /> {CONTACT.phone}
              </a>
            </div>
          </div>
          
          <div>
            <img 
              src="/images/spa-butler-plans.webp" 
              alt="Spa Butler Maintenance Membership Plans — Weekly $395, Bi-Weekly $295, Monthly $195, chemicals included" 
              className="w-full h-auto shadow-xl mb-6 rounded-lg"
              loading="lazy"
            />
          </div>
        </div>
        
        {/* Maintenance Packages */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#0E3D95]">Maintenance Membership Packages</h2>
              <p className="text-slate-600 mt-2">*Includes chemicals. Prices based on 25 mile radius.</p>
            </div>
            <a
              href="/brochures/spa-butler-brochure.png"
              download="Spa-Butler-Brochure.png"
              className="inline-flex items-center justify-center gap-2 bg-[#F88200] hover:bg-[#d96f00] text-white font-semibold px-6 py-3 shadow-md transition-colors self-start md:self-auto"
              data-testid="download-brochure-btn"
            >
              <Download size={18} /> Download Brochure
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {packages.map((pkg, idx) => (
              <motion.div 
                key={pkg.name} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: idx * 0.1 }} 
                className={`relative bg-white border-2 p-6 transition-all ${pkg.featured ? 'border-[#F88200] shadow-xl md:scale-105' : 'border-slate-200 hover:border-[#F88200] shadow-md'}`}
              >
                {pkg.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F88200] text-white text-xs font-bold uppercase px-3 py-1 tracking-wider">
                    Most Popular
                  </div>
                )}
                <h3 className="font-['Barlow_Condensed'] text-3xl font-bold text-[#0E3D95] mb-1">{pkg.name}</h3>
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">{pkg.drains}</p>
                <div className="mb-4 pb-4 border-b border-slate-200">
                  <p className="text-[#F88200] font-bold text-2xl leading-tight">{pkg.yearly}</p>
                  <p className="text-slate-600 text-sm mt-1">or <span className="font-bold text-[#0E3D95]">{pkg.monthly}</span></p>
                </div>
                <ul className="space-y-2">
                  {pkg.features.map(f => (
                    <li key={f} className="text-slate-600 text-sm flex items-start gap-2">
                      <span className="text-[#F88200] font-bold mt-0.5">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* 32 Point Inspection */}
        <div className="mb-12 bg-white p-8 shadow-lg border-t-4 border-[#0E3D95]">
          <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-[#0E3D95] mb-2 text-center">32 Point Inspection</h2>
          <p className="text-center text-slate-600 mb-6">Every service visit includes our comprehensive inspection checklist</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 max-w-5xl mx-auto">
            {inspectionPoints.map((point) => (
              <div key={point} className="flex items-start gap-2 text-slate-700">
                <span className="text-[#F88200] font-bold mt-0.5">✓</span>
                <span className="text-sm">{point}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Contact Form */}
        <div className="bg-slate-50 p-8">
          <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-[#0E3D95] mb-6 text-center">Get a Quote for Spa Butler Service</h2>
          <div className="max-w-xl mx-auto">
            <ContactForm />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default SpaButlerPage;
